import './assets/main.css';

import { createApp, provide, h } from 'vue';
import { createPinia } from 'pinia';
import piniaPluginPersistedState from 'pinia-plugin-persistedstate';
import { DefaultApolloClient } from '@vue/apollo-composable';
import * as Sentry from '@sentry/vue';

import App from './App.vue';
import router from './router';
import apolloClient from '@/services/apollo';

import permissionDirective from '@/directives/permission';

import {
  // AnimateOnScroll,
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionPanel,
  AutoComplete,
  Avatar,
  // AvatarGroup,
  Badge,
  BlockUI,
  Breadcrumb,
  Button,
  Card,
  Carousel,
  // CascadeSelect,
  Checkbox,
  Chip,
  // ColorPicker,
  Column,
  // ColumnGroup,
  ConfirmationService,
  ConfirmDialog,
  // ConfirmPopup,
  // ContextMenu,
  CsPrime,
  DataTable,
  DataView,
  // DatePicker,
  // DeferredContent,
  Dialog,
  DialogService,
  Divider,
  // Dock,
  Drawer,
  DynamicDialog,
  // Fieldset,
  FileUpload,
  // FloatLabel,
  // Galleria,
  // FocusTrap,
  Image,
  IconField,
  // Inplace,
  InputGroup,
  InputGroupAddon,
  InputIcon,
  // InputMask,
  InputNumber,
  InputOtp,
  InputText,
  // Knob,
  Listbox,
  Menu,
  // Menubar,
  Message,
  // MeterGroup,
  MultiSelect,
  // OrderList,
  // OrganizationChart,
  // OverlayBadge,
  Paginator,
  Panel,
  Password,
  // PickList,
  Popover,
  ProgressBar,
  ProgressSpinner,
  RadioButton,
  Rating,
  Ripple,
  // Row,
  Select,
  // SelectButton,
  // ScrollPanel,
  // ScrollTop,
  Skeleton,
  // Slider,
  // SpeedDial,
  // SplitButton,
  // Splitter,
  // SplitterPanel,
  Step,
  StepItem,
  StepList,
  StepPanel,
  StepPanels,
  Stepper,
  // StyleClass,
  // TieredMenu,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  // Terminal,
  Textarea,
  // Timeline,
  Toast,
  ToastService,
  ToggleButton,
  // ToggleSwitch,
  Toolbar,
  Tooltip,
  // Tree,
  // TreeSelect,
  // TreeTable,
  // VirtualScroller,
} from 'cc-prime';

import Preset from './preset';

export const app = createApp({
  setup() {
    provide(DefaultApolloClient, apolloClient);
  },

  render: () => h(App),
});

// Only init sentry on deployed environments
if (!window.location.href.includes('localhost')) {
  const environment = import.meta.env.VITE_ENVIRONMENT || 'production';

  const graphEndpoint = import.meta.env.VITE_GRAPHQL_ENDPOINT;
  const restEndpoint = import.meta.env.VITE_REST_ENDPOINT;
  const tracePropagationTargets = [graphEndpoint, restEndpoint];
  Sentry.init({
    app,
    environment,
    dsn: 'https://a1c6d5d9788a44ed1cb3746b2665eba5@o4508033360855040.ingest.us.sentry.io/4508844473974784',
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
      Sentry.feedbackIntegration({
        autoInject: false,
        colorScheme: 'system',
      }),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets,
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const pinia = createPinia();
pinia.use(piniaPluginPersistedState);
app.use(pinia);
app.use(router);

app.use(CsPrime, { ripple: true, unstyled: true, pt: Preset });
app.use(ConfirmationService);
app.use(DialogService);
app.use(ToastService);

// app.directive('animateonscroll', AnimateOnScroll);
// app.directive('focustrap', FocusTrap);
app.directive('ripple', Ripple);
// app.directive('styleclass', StyleClass);
app.directive('tooltip', Tooltip);

app.component('Accordion', Accordion);
app.component('AccordionContent', AccordionContent);
app.component('AccordionHeader', AccordionHeader);
app.component('AccordionPanel', AccordionPanel);
app.component('AutoComplete', AutoComplete);
app.component('Avatar', Avatar);
// app.component('AvatarGroup', AvatarGroup);
app.component('Badge', Badge);
app.component('BlockUI', BlockUI);
app.component('Breadcrumb', Breadcrumb);
app.component('Button', Button);
app.component('Card', Card);
app.component('Carousel', Carousel);
// app.component('CascadeSelect', CascadeSelect);
app.component('Checkbox', Checkbox);
app.component('Chip', Chip);
// app.component('ColorPicker', ColorPicker);
app.component('Column', Column);
// app.component('ColumnGroup', ColumnGroup);
app.component('ConfirmDialog', ConfirmDialog);
// app.component('ConfirmPopup', ConfirmPopup);
// app.component('ContextMenu', ContextMenu);
app.component('DataTable', DataTable);
app.component('DataView', DataView);
// app.component('DatePicker', DatePicker);
// app.component('DeferredContent', DeferredContent);
app.component('Dialog', Dialog);
app.component('Divider', Divider);
// app.component('Dock', Dock);
app.component('Drawer', Drawer);
app.component('DynamicDialog', DynamicDialog);
// app.component('Fieldset', Fieldset);
app.component('FileUpload', FileUpload);
// app.component('FloatLabel', FloatLabel);
// app.component('Galleria', Galleria);
app.component('Image', Image);
app.component('IconField', IconField);
// app.component('Inplace', Inplace);
app.component('InputGroup', InputGroup);
app.component('InputGroupAddon', InputGroupAddon);
app.component('InputIcon', InputIcon);
// app.component('InputMask', InputMask);
app.component('InputNumber', InputNumber);
app.component('InputOtp', InputOtp);
app.component('InputText', InputText);
app.component('Listbox', Listbox);
// app.component('Knob', Knob);
app.component('Menu', Menu);
// app.component('Menubar', Menubar);
app.component('Message', Message);
// app.component('MeterGroup', MeterGroup);
app.component('MultiSelect', MultiSelect);
// app.component('OrderList', OrderList);
// app.component('OrganizationChart', OrganizationChart);
// app.component('OverlayBadge', OverlayBadge);
app.component('Paginator', Paginator);
app.component('Panel', Panel);
app.component('Password', Password);
// app.component('PickList', PickList);
app.component('Popover', Popover);
app.component('ProgressBar', ProgressBar);
app.component('ProgressSpinner', ProgressSpinner);
app.component('RadioButton', RadioButton);
app.component('Rating', Rating);
// app.component('Row', Row);
// app.component('ScrollPanel', ScrollPanel);
// app.component('ScrollTop', ScrollTop);
app.component('Select', Select);
// app.component('SelectButton', SelectButton);
app.component('Skeleton', Skeleton);
// app.component('Slider', Slider);
// app.component('SpeedDial', SpeedDial);
// app.component('Splitter', Splitter);
// app.component('SplitterPanel', SplitterPanel);
// app.component('SplitButton', SplitButton);
app.component('Step', Step);
app.component('StepItem', StepItem);
app.component('StepList', StepList);
app.component('StepPanel', StepPanel);
app.component('StepPanels', StepPanels);
app.component('Stepper', Stepper);
app.component('Tab', Tab);
app.component('TabList', TabList);
app.component('TabPanel', TabPanel);
app.component('TabPanels', TabPanels);
app.component('Tabs', Tabs);
app.component('Tag', Tag);
app.component('Textarea', Textarea);
// app.component('Terminal', Terminal);
// app.component('TieredMenu', TieredMenu);
// app.component('Timeline', Timeline);
app.component('Toast', Toast);
app.component('ToggleButton', ToggleButton);
app.component('Toolbar', Toolbar);
// app.component('Tree', Tree);
// app.component('TreeSelect', TreeSelect);
// app.component('TreeTable', TreeTable);
// app.component('VirtualScroller', VirtualScroller);

app.directive('permission', permissionDirective);

app.mount('#app');
